// src/pages/Appli/AppliAdmin/ListingClients/components/AssociationDrawer.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import ReactDOM from "react-dom";

import { FaPlus, FaSave, FaSpinner, FaTimes } from "react-icons/fa";

import AssociationForm from "./AssociationForm";

import {
  associationToFormValues,
  buildAssociationPayload,
  validateAssociationForm,
} from "../utils/association.utils";

export default function AssociationDrawer({
  isOpen,
  association = null,
  saving = false,
  onClose,
  onSubmit,
}) {
  const drawerRef = useRef(null);

  const associationId = association?.id ?? null;

  const [formData, setFormData] = useState(() =>
    associationToFormValues(association),
  );

  const [initialData] = useState(() => associationToFormValues(association));

  const [errors, setErrors] = useState({});

  const isEditing = associationId !== null;

  const isDirty = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(initialData);
  }, [formData, initialData]);

  const requestClose = useCallback(() => {
    if (saving) return;

    if (
      isDirty &&
      !window.confirm(
        "Des modifications ne sont pas enregistrées. Fermer le formulaire ?",
      )
    ) {
      return;
    }

    onClose?.();
  }, [isDirty, onClose, saving]);

  useEffect(() => {
    if (!isOpen) return;

    const root = document.documentElement;
    const previousOverflow = root.style.overflow;

    root.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        requestClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    const focusTimer = window.setTimeout(() => {
      drawerRef.current?.querySelector('[name="nom_asso"]')?.focus();
    }, 150);

    return () => {
      root.style.overflow = previousOverflow;

      window.removeEventListener("keydown", handleKeyDown);

      window.clearTimeout(focusTimer);
    };
  }, [isOpen, requestClose]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (errors[name]) {
      setErrors((current) => {
        const nextErrors = { ...current };
        delete nextErrors[name];
        return nextErrors;
      });
    }
  };

  const handleDetailChange = (value) => {
    setFormData((current) => ({
      ...current,
      detail_asso: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationErrors = validateAssociationForm(formData);

    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);

      const firstErrorName = Object.keys(validationErrors)[0];

      window.setTimeout(() => {
        drawerRef.current?.querySelector(`[name="${firstErrorName}"]`)?.focus();
      }, 0);

      return;
    }

    const payload = buildAssociationPayload(formData);

    await onSubmit?.({
      associationId,
      payload,
      isEditing,
    });
  };

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      requestClose();
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-slate-950/60 backdrop-blur-sm"
      onMouseDown={handleOverlayClick}
      role="presentation"
    >
      <aside
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="association-drawer-title"
        className="ml-auto flex h-full w-full max-w-4xl flex-col bg-slate-50 shadow-2xl dark:bg-slate-950"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="shrink-0 bg-gradient-to-r from-blue-600 via-violet-600 to-fuchsia-600 px-4 py-4 text-white sm:px-6">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="mb-1 flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-white/75">
                {isEditing ? (
                  <>Dossier nº {association.id}</>
                ) : (
                  <>Nouveau dossier</>
                )}
              </div>

              <h2
                id="association-drawer-title"
                className="truncate text-xl font-bold sm:text-2xl"
              >
                {isEditing
                  ? `Modifier ${association.nom_asso}`
                  : "Ajouter une association"}
              </h2>

              <p className="mt-1 text-sm text-white/80">
                {isEditing
                  ? "Mettez à jour les informations et le suivi commercial."
                  : "Créez un nouveau prospect, client ou partenaire professionnel."}
              </p>
            </div>

            <button
              type="button"
              onClick={requestClose}
              disabled={saving}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/15 text-white transition hover:bg-white/25 disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Fermer le formulaire"
              title="Fermer"
            >
              <FaTimes />
            </button>
          </div>
        </header>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div
            className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6"
            style={{
              WebkitOverflowScrolling: "touch",
            }}
          >
            <AssociationForm
              key={`association-form-${associationId ?? "new"}`}
              formData={formData}
              errors={errors}
              disabled={saving}
              onChange={handleChange}
              onDetailChange={handleDetailChange}
            />
          </div>

          <footer className="shrink-0 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95 sm:px-6">
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-xs text-slate-500 dark:text-slate-400">
                {isDirty
                  ? "Modifications non enregistrées"
                  : "Aucune modification en attente"}
              </div>

              <div className="flex flex-col-reverse gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={requestClose}
                  disabled={saving}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  <FaTimes />
                  Annuler
                </button>

                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-violet-600/20 transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving ? (
                    <>
                      <FaSpinner className="animate-spin" />
                      Enregistrement…
                    </>
                  ) : isEditing ? (
                    <>
                      <FaSave />
                      Enregistrer
                    </>
                  ) : (
                    <>
                      <FaPlus />
                      Ajouter l’association
                    </>
                  )}
                </button>
              </div>
            </div>
          </footer>
        </form>

        <style>{`
          .ql-container {
            min-height: 170px;
            border: 0 !important;
            font-size: 14px;
          }

          .ql-editor {
            min-height: 170px;
          }

          .ql-toolbar {
            border-width: 0 0 1px 0 !important;
            border-color: #cbd5e1 !important;
          }

          .dark .ql-toolbar {
            border-color: #334155 !important;
          }

          .dark .ql-toolbar .ql-stroke {
            stroke: #cbd5e1;
          }

          .dark .ql-toolbar .ql-fill {
            fill: #cbd5e1;
          }

          .dark .ql-toolbar .ql-picker {
            color: #cbd5e1;
          }

          .dark .ql-editor {
            color: #f1f5f9;
          }

          .dark .ql-editor.ql-blank::before {
            color: #64748b;
          }
        `}</style>
      </aside>
    </div>,
    document.body,
  );
}
